import React, { useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import ChartImpact from "../components/ChartImpact";

// Catégories de stats
const CATEGORIZED_STATS = {
  "Dégâts": [
    "damageDealtToBuildings",
    "damageDealtToObjectives",
    "damageDealtToTurrets",
    "totalDamageDealt",
    "totalDamageDealtToChampions",
  ],
  Tankiness: ["totalDamageTaken", "damageSelfMitigated"],
  "Soins et Boucliers": [
    "effectiveHealAndShielding",
    "totalDamageShieldedOnTeammates",
    "totalHealsOnTeammates",
    "totalHeal",
  ],
  "Contrôle": [
    "enemyChampionImmobilizations",
    "timeCCingOthers",
    "totalTimeCCDealt",
  ],
  "Kills / Participation": [
    "deaths",
    "soloKills",
    "killParticipation",
    "pickKillWithAlly",
    "immobilizeAndKillWithAlly",
    "killAfterHiddenWithAlly",
  ],
  "Économie / CS": [
    "goldEarned",
    "totalMinionsKilled",
    "totalAllyJungleMinionsKilled",
    "totalEnemyJungleMinionsKilled",
    "turretKills",
  ],
  "Early impact": [
    "earlyTakedowns",
    "earlyCs10",
    "earlyCsLead",
    "earlyLevelLead",
    "earlyPlatesTaken",
    "killsOnLanersEarlyJungleAsJungler",
    "killsOnOtherLanesEarlyJungleAsLaner",
  ],
  Skillshots: ["skillshotsDodged", "skillshotsHit"],
  Vision: ["controlWardsPlaced", "wardsGuarded", "wardsKilled", "wardsPlaced"],
};

const STAT_LABELS = {
  controlWardsPlaced: "Balises de contrôle posées",
  damageDealtToBuildings: "Dégâts aux bâtiments",
  damageDealtToObjectives: "Dégâts aux objectifs",
  damageDealtToTurrets: "Dégâts aux tourelles",
  damageSelfMitigated: "Dégâts auto-mitigés",
  deaths: "Morts",
  effectiveHealAndShielding: "Soins et boucliers effectifs",
  enemyChampionImmobilizations: "Immobilisations d'ennemis",
  goldEarned: "Or gagné",
  immobilizeAndKillWithAlly: "Immobilisation + kill avec un allié",
  killAfterHiddenWithAlly: "Kill après s'être caché avec un allié",
  killParticipation: "Participation aux kills",
  pickKillWithAlly: "Kill ciblé avec un allié",
  skillshotsDodged: "Skillshots esquivés",
  skillshotsHit: "Skillshots touchés",
  soloKills: "Kills solo",
  timeCCingOthers: "Temps de contrôle de foule",
  totalAllyJungleMinionsKilled: "Monstres alliés de jungle tués",
  totalDamageDealt: "Dégâts totaux infligés",
  totalDamageDealtToChampions: "Dégâts aux champions",
  totalDamageShieldedOnTeammates: "Boucliers appliqués aux alliés",
  totalDamageTaken: "Dégâts subis",
  totalEnemyJungleMinionsKilled: "Monstres ennemis de jungle tués",
  totalHeal: "Soins totaux",
  totalHealsOnTeammates: "Soins appliqués aux alliés",
  totalMinionsKilled: "Sbires tués",
  totalTimeCCDealt: "Durée totale de CC infligé",
  turretKills: "Tourelles détruites",
  wardsGuarded: "Balises protégées",
  wardsKilled: "Balises ennemies détruites",
  wardsPlaced: "Balises posées",
  earlyTakedowns: "Kills/assists early",
  earlyCs10: "CS a 10 min",
  earlyCsLead: "Avance CS max",
  earlyLevelLead: "Avance niveau max",
  earlyPlatesTaken: "Plaques prises",
  killsOnLanersEarlyJungleAsJungler: "Ganks early du jungler",
  killsOnOtherLanesEarlyJungleAsLaner: "Kills early hors lane",
};

const ImpactPage = () => {
  const { summonerName, tag } = useParams();
  const location = useLocation();

  // données envoyées depuis LightImpactPage
  const data = location.state?.data;

  // prépa données de base
  const impactObj = data?.impact || {};
  const globalImpact = Object.entries(impactObj).map(([stat, value]) => ({
    stat,
    value,
  }));

  const champions = data?.champions || [];
  const role = data?.role ?? "";
  const gamesCount = data?.games_analyzed ?? 0;
  const winrate =
    typeof data?.winrate === "number" ? data.winrate : null;

  // HOOKS
  const [selectedChampion, setSelectedChampion] = useState(null);
  const [selectedStats, setSelectedStats] = useState(
    globalImpact.map((d) => d.stat)
  );
  const [expandedCategories, setExpandedCategories] = useState(() =>
    Object.keys(CATEGORIZED_STATS).reduce((acc, cat) => {
      acc[cat] = true;
      return acc;
    }, {})
  );
  const [sortDesc, setSortDesc] = useState(true); // 🔹 tri descendant par défaut

  // si pas de data (refresh direct)
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center px-4">
        <p className="mb-4 text-slate-300 text-center">
          Impossible de retrouver les données d&apos;analyse.
          <br />
          Retourne à l&apos;accueil pour relancer une analyse.
        </p>
        <Link
          to="/"
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm"
        >
          Retour à l&apos;accueil
        </Link>
      </div>
    );
  }

  // champion sélectionné
  const selectedChampionData =
    selectedChampion != null
      ? champions.find((c) => c.champion === selectedChampion) || null
      : null;

  // impact de base (global ou champion)
  const baseImpactArray = (() => {
    if (selectedChampionData && selectedChampionData.impact) {
      return Object.entries(selectedChampionData.impact).map(
        ([stat, value]) => ({ stat, value })
      );
    }
    return globalImpact;
  })();

  // impact filtré + trié
  const filteredData = baseImpactArray
    .filter((d) => selectedStats.includes(d.stat))
    .sort((a, b) => {
      const va = Number(a.value) || 0;
      const vb = Number(b.value) || 0;
      return sortDesc ? vb - va : va - vb;
    });

  const toggleStat = (statName) => {
    setSelectedStats((prev) =>
      prev.includes(statName)
        ? prev.filter((s) => s !== statName)
        : [...prev, statName]
    );
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleCategoryCheckbox = (stats) => {
    const isAllSelected = stats.every((s) => selectedStats.includes(s));
    setSelectedStats((prev) =>
      isAllSelected
        ? prev.filter((s) => !stats.includes(s))
        : [...new Set([...prev, ...stats])]
    );
  };

  const contextLabel = selectedChampion
    ? `Impact détaillé sur ${selectedChampion}`
    : "Impact détaillé (tous champions)";

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* HEADER */}
      <header className="w-full border-b border-slate-800 bg-slate-950/80 backdrop-blur">
                <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
                  <Link to="/" className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600/80 text-sm font-bold">
                      IG
                    </span>
                    <span className="font-semibold text-lg tracking-tight">
                      ImpactGame
                    </span>
                  </Link>
      
                  <nav className="flex gap-4 text-sm text-slate-300">
                    <Link to="/" className="hover:text-white transition">
                      Accueil
                    </Link>
                    <Link to="/about" className="hover:text-white transition">À propos</Link>
                  </nav>
                </div>
              </header>

      {/* CONTENU */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-1">
          Analyse détaillée – {summonerName}#{tag}
        </h1>
        <p className="text-xs text-slate-400 mb-6">
          Compare tes stats d&apos;impact à celles de ton vis-à-vis, et filtre
          par champion ou par type de statistique.
        </p>

        {/* Infos globales */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl px-4 py-3">
            <div className="text-xs text-slate-400 uppercase">
              Rôle détecté
            </div>
            <div className="text-lg font-semibold">
              {role ? role.toUpperCase() : "N/A"}
            </div>
          </div>
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl px-4 py-3">
            <div className="text-xs text-slate-400 uppercase">
              Games analysées
            </div>
            <div className="text-lg font-semibold">{gamesCount}</div>
          </div>
          <div className="bg-slate-900/80 border border-slate-800 rounded-xl px-4 py-3">
            <div className="text-xs text-slate-400 uppercase">
              Winrate (échantillon)
            </div>
            <div className="text-lg font-semibold">
              {winrate !== null ? `${winrate}%` : "N/A"}
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* COLONNE GAUCHE : champions + filtres stats */}
          <div className="lg:w-1/3 flex flex-col gap-4">
            {/* Filtre champion */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4">
              <h2 className="text-sm font-semibold text-slate-200 mb-3">
                Filtrer par champion
              </h2>

              <button
                type="button"
                onClick={() => setSelectedChampion(null)}
                className={`w-full mb-3 px-3 py-2 rounded-xl text-xs border transition ${
                  selectedChampion === null
                    ? "bg-blue-600/30 border-blue-400 text-slate-50"
                    : "bg-slate-950/70 border-slate-700 hover:bg-slate-900 text-slate-200"
                }`}
              >
                Tous les champions
              </button>

              {champions.length === 0 && (
                <p className="text-xs text-slate-500">
                  Pas encore assez de données par champion.
                </p>
              )}

              <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                {champions.map((c) => {
                  const isActive = selectedChampion === c.champion;
                  const imgUrl = `https://ddragon.leagueoflegends.com/cdn/14.21.1/img/champion/${c.champion}.png`;

                  return (
                    <button
                      key={c.champion}
                      type="button"
                      onClick={() =>
                        setSelectedChampion((prev) =>
                          prev === c.champion ? null : c.champion
                        )
                      }
                      className={`w-full flex items-center gap-3 text-left rounded-xl px-3 py-2 transition border
                        ${
                          isActive
                            ? "bg-blue-600/30 border-blue-400"
                            : "bg-slate-950/70 border-slate-800 hover:bg-slate-900"
                        }`}
                    >
                      <div className="h-10 w-10 rounded-lg overflow-hidden bg-slate-800 flex-shrink-0">
                        <img
                          src={imgUrl}
                          alt={c.champion}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      <div className="flex flex-col">
                        <span className="text-sm text-slate-200 font-medium">
                          {c.champion}
                        </span>
                        <span className="text-xs text-slate-400">
                          {c.games} games • {c.winrate}% WR
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Filtre stats */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 max-h-[50vh] overflow-y-auto">
              <h2 className="text-sm font-semibold text-slate-200 mb-3">
                Filtrer les statistiques
              </h2>

              {Object.entries(CATEGORIZED_STATS).map(([category, stats]) => (
                <div
                  key={category}
                  className="bg-slate-950/70 border border-slate-800 rounded-lg mb-3"
                >
                  <div className="flex justify-between items-center p-2 border-b border-slate-800 bg-slate-900/80">
                    <button
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className="cursor-pointer text-xs font-semibold text-cyan-400"
                    >
                      {category} {expandedCategories[category] ? "▲" : "▼"}
                    </button>
                    <input
                      type="checkbox"
                      checked={stats.every((s) => selectedStats.includes(s))}
                      ref={(input) => {
                        if (input) {
                          const isAll = stats.every((s) =>
                            selectedStats.includes(s)
                          );
                          const isNone = stats.every(
                            (s) => !selectedStats.includes(s)
                          );
                          input.indeterminate = !isAll && !isNone;
                        }
                      }}
                      onChange={() => handleCategoryCheckbox(stats)}
                      className="accent-cyan-400 cursor-pointer"
                      title="Tout sélectionner / désélectionner"
                    />
                  </div>

                  {expandedCategories[category] && (
                    <div className="flex flex-col gap-1 p-2 text-xs">
                      {stats.map((stat) => (
                        <label
                          key={stat}
                          className="flex items-center space-x-2"
                        >
                          <input
                            type="checkbox"
                            checked={selectedStats.includes(stat)}
                            onChange={() => toggleStat(stat)}
                            className="accent-cyan-400"
                          />
                          <span>{STAT_LABELS[stat] || stat}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* COLONNE DROITE : chart */}
          <div className="lg:w-2/3 bg-slate-900/80 border border-slate-800 rounded-2xl p-4">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-semibold text-slate-200">
                {contextLabel}
              </h2>
              <div className="flex flex-col items-end gap-1">
                <span className="text-[11px] text-slate-500">
                  Différences d&apos;impact vs ton vis-à-vis (en % de
                  l&apos;équipe)
                </span>
                <label className="flex items-center gap-1 text-[11px] text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!sortDesc}
                    onChange={() => setSortDesc((prev) => !prev)}
                    className="accent-cyan-400"
                  />
                  <span>Inverser le tri</span>
                </label>
              </div>
            </div>

            {filteredData.length === 0 ? (
              <p className="text-xs text-slate-400">
                Aucune statistique sélectionnée pour ce contexte.
              </p>
            ) : (
              <ChartImpact data={filteredData} />
            )}
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-slate-800 bg-slate-950/90 mt-6">
        <div className="max-w-5xl mx-auto px-4 py-4 text-xs text-slate-400 text-center sm:text-left">
          © {new Date().getFullYear()} ImpactGame · Projet non affilié à Riot
          Games.
        </div>
      </footer>
    </div>
  );
};

export default ImpactPage;
